import React, { useState } from 'react';
import { useMapStore } from '../../store/useMapStore';

interface CommentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  selectedLocation: {
    lat: number;
    lng: number;
    address?: string;
  } | null;
  onMarkerSaved?: () => void;
}

const CommentDialog: React.FC<CommentDialogProps> = ({ isOpen, onClose, selectedLocation, onMarkerSaved }) => {
  const [locationType, setLocationType] = useState('Apartment');
  const [unitNumber, setUnitNumber] = useState('');
  const [comment, setComment] = useState('');
  const { saveMarker } = useMapStore();

  // Debug logging
  React.useEffect(() => {
    if (isOpen) {
      console.log('CommentDialog opened with location:', selectedLocation);
    }
  }, [isOpen, selectedLocation]);

  if (!isOpen || !selectedLocation) {
    console.log('CommentDialog not rendering - isOpen:', isOpen, 'selectedLocation:', selectedLocation);
    return null;
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Saving marker with data:', { comment, locationType, unitNumber });
    console.log('Location to save:', selectedLocation);
    saveMarker({ comment, locationType, unitNumber: unitNumber.trim() || undefined });
    setComment('');
    setUnitNumber('');
    setLocationType('Apartment');
    onClose();
    // Notify parent component that marker was saved
    if (onMarkerSaved) {
      onMarkerSaved();
    }
  };

  const handleClose = () => {
    setComment('');
    setUnitNumber('');
    setLocationType('Apartment');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-4xl max-h-[90vh] rounded-lg shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-start p-4 sm:p-6 border-b">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Add Comment</h2>
            <p className="text-xs sm:text-sm text-gray-600 mt-1 break-words">
              {selectedLocation.address || `Location: ${selectedLocation.lat.toFixed(6)}, ${selectedLocation.lng.toFixed(6)}`}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 ml-2 flex-shrink-0"
          >
            <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <form onSubmit={handleSubmit} className="h-full flex flex-col space-y-4">
            {/* Location Type Dropdown */}
            <div>
              <label htmlFor="locationType" className="block text-sm font-medium text-gray-700 mb-2">
                Location Type
              </label>
              <select
                id="locationType"
                value={locationType}
                onChange={e => setLocationType(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                required
              >
                <option value="Apartment">Apartment</option>
                <option value="Store">Store</option>
              </select>
            </div>

            {/* Unit Number */}
            <div>
              <label htmlFor="unitNumber" className="block text-sm font-medium text-gray-700 mb-2">
                Unit Number (optional)
              </label>
              <input
                id="unitNumber"
                type="text"
                value={unitNumber}
                onChange={e => setUnitNumber(e.target.value)}
                placeholder="Enter unit number if applicable"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
            </div>

            {/* Comment */}
            <div className="flex-1">
              <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-2">
                Your Comment
              </label>
              <textarea
                id="comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add your comment about this location..."
                className="w-full h-32 sm:h-40 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none text-sm"
                required
              />
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-end gap-3 pt-6 border-t">
              <button
                type="button"
                onClick={handleClose}
                className="w-full sm:w-auto px-6 py-3 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!comment.trim()}
                className="w-full sm:w-auto px-6 py-3 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
              >
                Save Comment
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CommentDialog; 